<template>
  <v-container class="py-4">
    <v-card class="pa-4 mb-4">
      <v-row align="center" justify="space-between">
        <v-col cols="12" md="8">
          <h2 class="mb-1">Koneko A (v1 prototype)</h2>
          <koneko-a-month-navigator
            :title="calendarMonthDisplayTitle"
            :show-this-month-hint="isViewingCurrentCalendarMonth"
            @prev="goToPreviousKonekoMonth"
            @next="goToNextKonekoMonth"
          />
          <div class="subtitle-2 grey--text text--darken-1">
            Monthly budgeting progress with admin + individual views.
          </div>
          <div class="caption mt-2">
            <strong>{{ weekScopeHeading }}</strong>
          </div>
          <div class="caption">
            Mode:
            <strong v-if="viewAsPreviewActive"
              >Preview: {{ viewAsPreviewLabel }}</strong
            >
            <strong v-else-if="effectiveAdminView">Admin (all members)</strong>
            <strong v-else>Member (single user)</strong>
          </div>
          <p
            v-if="
              signedInIsKonekoAdmin &&
              !viewAsPreviewActive &&
              !effectiveAdminView
            "
            class="caption warning--text text--darken-2 mt-1 mb-0"
          >
            You are viewing your own scope only.
            <router-link
              :to="{
                path: '/koneko-a',
                query: {...$route.query, scope: undefined},
              }"
            >
              View all members
            </router-link>
          </p>
        </v-col>
        <v-col cols="12" md="4" class="text-md-right">
          <v-chip class="ma-1" color="indigo" dark>
            Build {{ buildStamp }}
          </v-chip>
          <v-chip class="ma-1" outlined>
            {{ versionStamp }}
          </v-chip>
          <v-btn
            v-if="canManageKonekoAccess && !viewAsPreviewActive"
            class="ma-1"
            small
            outlined
            :to="{name: 'KonekoAAccess'}"
          >
            Access control
          </v-btn>
          <v-btn
            class="ma-1"
            small
            outlined
            color="primary"
            :loading="loading"
            @click="refreshNow"
          >
            Refresh
          </v-btn>
          <div class="caption mt-1" v-if="lastUpdatedAt">
            Last updated: {{ lastUpdatedAt }}
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-alert v-if="viewAsPreviewActive" type="warning" outlined class="mb-3">
      Simulating <strong>{{ viewAsPreviewLabel }}</strong
      >'s view — only panels and actions they are allowed appear below. Change
      via <strong>View as</strong> in the nav drawer or Dashboard.
    </v-alert>
    <dev-api-wiring-hint :force="Boolean(loadError)" />
    <v-alert v-if="loading" type="info" outlined>
      Loading Koneko A data from NekoHR API...
    </v-alert>
    <v-alert v-if="loadError" type="error" outlined>
      {{ loadError }}
    </v-alert>
    <v-alert
      v-if="konekoBudgetApiError"
      type="error"
      outlined
      dismissible
      class="mb-3"
      @input="konekoBudgetApiError = ''"
    >
      {{ konekoBudgetApiError }}
    </v-alert>
    <v-alert
      v-if="konekoBudgetSaveOk"
      type="success"
      outlined
      dismissible
      class="mb-3"
      @input="konekoBudgetSaveOk = ''"
    >
      {{ konekoBudgetSaveOk }}
    </v-alert>
    <v-alert
      v-if="konekoReassignNotice"
      type="success"
      outlined
      dismissible
      class="mb-3"
      @input="konekoReassignNotice = ''"
    >
      {{ konekoReassignNotice }}
    </v-alert>

    <v-dialog
      v-model="konekoReassignConfirm.show"
      max-width="480"
      persistent
      @keydown.esc="cancelKonekoTimesheetReassign"
    >
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          Reassign timesheet line?
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Move
            <strong>{{ konekoReassignConfirm.hoursLabel }}</strong>
            on
            <strong>{{ konekoReassignConfirm.dateLabel }}</strong>
          </p>
          <p class="mb-0">
            From
            <strong>{{ konekoReassignConfirm.fromProject }}</strong>
            →
            <strong>{{ konekoReassignConfirm.newProjectName }}</strong>
          </p>
          <p class="caption grey--text text--darken-1 mt-3 mb-0">
            The line will disappear from the current project and appear under
            the new one after you confirm.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="Boolean(timesheetReassignSavingId)"
            @click="cancelKonekoTimesheetReassign"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="Boolean(timesheetReassignSavingId)"
            @click="confirmKonekoTimesheetReassign"
          >
            Move time
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-alert
      v-if="staleKonekoFrontendBundle"
      type="warning"
      outlined
      class="mb-3"
    >
      This page is running an old cached build ({{ buildStamp }}). Hard refresh
      (Ctrl+Shift+R) or use a private window so you can see
      <strong>My budget</strong>.
    </v-alert>
    <v-progress-linear
      v-if="!loading && !konekoHeavyUiReady"
      indeterminate
      color="primary"
      class="mb-2"
      height="3"
    />
    <v-expansion-panels
      v-if="konekoHeavyUiReady && !loading"
      v-model="konekoAOpenPanels"
      multiple
      class="mb-4"
    >
      <v-expansion-panel v-if="showMyBudgetPanel">
        <v-expansion-panel-header>
          My budget ({{ calendarMonthKey }})
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="caption grey--text text--darken-1 mb-3">
            Personal project caps for
            <strong>{{ myBudgetEditorUser.displayName }}</strong>
            in the month selected above.
            <template v-if="konekoBudgetBackendUsesApi">
              Saved to NekoHR; updates your progress bars and member breakdown
              after Save.
              <span class="grey--text text--darken-1">
                · {{ myBudgetSubmissionLabel }}
              </span>
            </template>
            <template v-else>
              Saved in this browser only; updates your progress bars and member
              breakdown after Save.
            </template>
            Use <strong>Remove</strong> to drop a project from this list, then
            Save. You can add it back with Add row.
          </p>
          <v-alert
            v-if="konekoBudgetApiError"
            type="error"
            dense
            outlined
            class="mb-3"
          >
            {{ konekoBudgetApiError }}
          </v-alert>
          <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
            <v-btn small color="primary" @click="saveMyMemberTargetsDraft">
              {{ konekoBudgetBackendUsesApi ? "Save" : "Save local" }}
            </v-btn>
            <v-btn
              small
              outlined
              color="grey darken-1"
              @click="resetMyMemberTargetsDraft"
            >
              Reset to config
            </v-btn>
          </div>
          <v-simple-table
            dense
            class="koneko-budget-cap-table my-budget-editor-table"
          >
            <thead>
              <tr>
                <th class="koneko-col-project">Project</th>
                <th class="koneko-col-hours">Min (HH:MM)</th>
                <th class="koneko-col-hours">Max (HH:MM)</th>
                <th class="koneko-col-remove text-center">Remove</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in myBudgetEditorRows"
                :key="`my-budget-${row.project}`"
              >
                <td>{{ row.project }}</td>
                <td style="max-width: 140px">
                  <v-text-field
                    dense
                    hide-details
                    outlined
                    placeholder="0:00"
                    :value="row.low"
                    @input="
                      updateMyMemberTargetsDraft(row.project, 'low', $event)
                    "
                    @blur="
                      normalizeMyMemberTargetsDraftField(row.project, 'low')
                    "
                  />
                </td>
                <td style="max-width: 140px">
                  <v-text-field
                    dense
                    hide-details
                    outlined
                    placeholder="0:00"
                    :value="row.high"
                    @input="
                      updateMyMemberTargetsDraft(row.project, 'high', $event)
                    "
                    @blur="
                      normalizeMyMemberTargetsDraftField(row.project, 'high')
                    "
                  />
                </td>
                <td class="text-center">
                  <v-btn
                    icon
                    x-small
                    color="grey darken-1"
                    :aria-label="`Remove ${row.project} from My budget`"
                    @click="removeMyBudgetEditorProject(row.project)"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr class="font-weight-medium grey lighten-4">
                <td>Total</td>
                <td>{{ myBudgetEditorColumnTotals.minLabel }}</td>
                <td>{{ myBudgetEditorColumnTotals.maxLabel }}</td>
                <td />
              </tr>
            </tbody>
          </v-simple-table>
          <div class="d-flex align-end flex-wrap mt-3" style="gap: 8px">
            <v-combobox
              :key="`my-budget-add-project-${calendarMonthKey}-${myBudgetProjectNameSuggestions.length}`"
              v-model="myBudgetNewProjectName"
              :items="myBudgetProjectNameSuggestions"
              label="Add project"
              dense
              hide-details
              outlined
              clearable
              style="max-width: 320px; flex: 1 1 200px"
            />
            <v-btn
              small
              outlined
              color="primary"
              :disabled="!String(myBudgetNewProjectName || '').trim()"
              @click="
                addMyBudgetEditorProject(myBudgetNewProjectName);
                myBudgetNewProjectName = '';
              "
            >
              Add row
            </v-btn>
          </div>
          <KonekoABudgetHistoryPanel
            ref="konekoBudgetHistory"
            :calendar-month-key="calendarMonthKey"
            :enabled="konekoBudgetBackendUsesApi"
            :subject-username="
              (myBudgetEditorUser && myBudgetEditorUser.username) || ''
            "
            :for-self="myBudgetHistoryForSelf"
            :project-order="myBudgetEditorProjectNames"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel v-if="hasKonekoCap(KONEKO_CAP.konekoProjectSnapshot)">
        <v-expansion-panel-header>
          Project Budget Snapshot
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <KonekoAProjectBudgetSnapshotPanels
            :rows="projectBudgetSnapshotRows"
            :calendar-month-key="calendarMonthKey"
            :timesheet-month-key="timesheetMonthKey"
            :snapshot-uses-timesheet-fallback="snapshotUsesTimesheetFallback"
            :is-future-calendar-month="isFutureCalendarMonth"
            :calendar-month-display-title="calendarMonthDisplayTitle"
            :month-elapsed-percent="monthElapsedPercent"
            :week-label="weekLabel"
            :format-hours-cell="formatHoursCell"
            :format-signed-hours="formatSignedHours"
            :on-snapshot-baseline-change="
              setProjectSnapshotBudgetBaselineForProject
            "
            :project-options="konekoProjectOptions"
            :reassign-saving-id="timesheetReassignSavingId"
            :hours-saving-id="timesheetHoursSavingId"
            :can-reassign-entry="canReassignKonekoEntry"
            :can-edit-hours="canEditKonekoTimesheetEntry"
            :is-editing-hours="isEditingTimesheetHours"
            :hours-edit-value="timesheetHoursEdit.value"
            :show-reassign-ui="showKonekoReassignUi"
            @reassign="
              (entry, pid, pname, fromProject) =>
                reassignKonekoTimesheetEntry(entry, pid, pname, {fromProject})
            "
            @edit-hours="startKonekoTimesheetHoursEdit"
            @commit-hours-edit="commitKonekoTimesheetHoursEdit"
            @cancel-hours-edit="cancelKonekoTimesheetHoursEdit"
            @update:hoursEditValue="timesheetHoursEdit.value = $event"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel v-if="hasKonekoCap(KONEKO_CAP.konekoCrossComparison)">
        <v-expansion-panel-header> Cross Comparison </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
            <span class="caption grey--text text--darken-1">Sort by</span>
            <v-btn-toggle
              v-model="crossComparisonSortBy"
              mandatory
              dense
              color="primary"
              @change="persistCrossComparisonSortBy"
            >
              <v-btn small value="budgetDelta">Budget Δ (default)</v-btn>
              <v-btn small value="name">Name</v-btn>
            </v-btn-toggle>
          </div>
          <p class="caption grey--text text--darken-1 mb-3">
            Compares summed personal planned hours from
            <code>konekoATargets</code> against monthly budget ranges in
            <code>konekoAMonthlyProjectBudgets</code>. Expand a project to see
            each member’s <strong>declared</strong> cap, <strong>month</strong>
            hours (with bar), and expandable timesheet lines. Projects with
            logged or declared activity but no org budget appear in
            <strong>Budget Editor</strong> for setup.
          </p>
          <v-expansion-panels multiple class="cross-comparison-nested-panels">
            <v-expansion-panel
              v-for="row in sortedCrossComparisonRows"
              :key="'cc-' + row.project"
            >
              <v-expansion-panel-header
                class="cross-compare-row-header align-start py-2"
              >
                <div
                  class="d-flex flex-wrap align-center"
                  style="gap: 6px 12px; max-width: 100%"
                >
                  <span
                    class="subtitle-2 font-weight-medium text-truncate"
                    style="max-width: 280px"
                    :class="budgetStatusTextClass(row.barStatus)"
                    >{{ row.project }}</span
                  >
                  <span class="caption">
                    Est
                    <strong>{{
                      formatHoursCell(row.overallEstimatedHours)
                    }}</strong>
                  </span>
                  <span class="caption grey--text text--darken-1"
                    >Min {{ row.budgetMin }}</span
                  >
                  <span class="caption grey--text text--darken-1"
                    >Max {{ row.budgetMax }}</span
                  >
                  <span
                    class="caption font-weight-medium"
                    :class="budgetStatusTextClass(row.barStatus)"
                    >{{ row.status }}</span
                  >
                  <span
                    class="caption"
                    :class="budgetStatusTextClass(row.barStatus)"
                    >Δ {{ row.deltaVsMaxLabel }}</span
                  >
                  <span
                    class="caption"
                    :class="budgetStatusTextClass(row.barStatus)"
                    >{{ row.usageLabel }}</span
                  >
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-2">
                <div class="caption font-weight-medium mb-1">
                  Hours by member
                </div>
                <p class="caption grey--text text--darken-1 mb-2">
                  Declared = cap used in the estimate (target, else high, else
                  low). Month = logged this calendar month on that project.
                  Click a name for timesheet lines and notes.
                </p>
                <div v-if="row.budgetBar" class="snapshot-header-bar-wrap mb-3">
                  <KonekoBudgetRangeBar
                    :current-decimal="row.budgetBar.current"
                    :low-decimal="row.budgetBar.low"
                    :high-decimal="row.budgetBar.high"
                    :target-decimal="row.budgetBar.target"
                    :month-elapsed-percent="monthElapsedPercent"
                    :status="row.barStatus"
                  />
                </div>
                <KonekoAMemberHoursDrilldownTable
                  v-if="row.contributors && row.contributors.length"
                  :project-name="row.project"
                  :rows="row.contributors"
                  :calendar-month-key="calendarMonthKey"
                  :month-elapsed-percent="monthElapsedPercent"
                  :format-hours-cell="formatHoursCell"
                  :show-week-column="false"
                  :show-declared-column="true"
                  :project-options="konekoProjectOptions"
                  :reassign-saving-id="timesheetReassignSavingId"
                  :can-reassign-entry="canReassignKonekoEntry"
                  :show-reassign-ui="showKonekoReassignUi"
                  @reassign="
                    (entry, pid, pname, fromProject) =>
                      reassignKonekoTimesheetEntry(entry, pid, pname, {
                        fromProject,
                      })
                  "
                />
                <p v-else class="caption grey--text text--darken-1 mb-0">
                  No per-member activity for this project.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-simple-table
            v-if="crossComparisonRows.length"
            dense
            class="cross-comparison-totals mt-3"
          >
            <thead>
              <tr class="caption grey--text text--darken-1">
                <th></th>
                <th class="text-left">Est (declared)</th>
                <th class="text-left">Min</th>
                <th class="text-left">Max</th>
                <th class="text-left">Month logged</th>
              </tr>
            </thead>
            <tbody>
              <tr class="font-weight-medium grey lighten-4">
                <td>Total (all projects above)</td>
                <td>{{ crossComparisonColumnTotals.estimateLabel }}</td>
                <td>{{ crossComparisonColumnTotals.minLabel }}</td>
                <td>{{ crossComparisonColumnTotals.maxLabel }}</td>
                <td>{{ crossComparisonColumnTotals.loggedLabel }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel v-if="hasKonekoCap(KONEKO_CAP.konekoOrgBudgetEditor)">
        <v-expansion-panel-header>
          Budget Editor ({{ calendarMonthKey }})
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="caption grey--text text--darken-1 mb-3">
            <template v-if="konekoBudgetOrgUsesApi">
              Org-wide monthly project bands for {{ calendarMonthKey }}. Changes
              save automatically when you leave a Min/Max field (or click Save).
              Expand a project to see who logged time and their timesheet lines.
            </template>
            <template v-else>
              Local-only month budget overrides for your browser profile.
            </template>
          </p>
          <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
            <span class="caption grey--text text--darken-1">Sort by</span>
            <v-btn-toggle
              v-model="budgetEditorSortBy"
              mandatory
              dense
              color="primary"
              @change="persistBudgetEditorSortBy"
            >
              <v-btn small value="budgetDelta">Over budget (Δ)</v-btn>
              <v-btn small value="name">Name</v-btn>
            </v-btn-toggle>
            <v-btn
              small
              color="primary"
              :loading="monthBudgetSaving"
              @click="saveMonthBudgetDraft"
            >
              {{ konekoBudgetOrgUsesApi ? "Save" : "Save Local" }}
            </v-btn>
            <v-btn
              small
              outlined
              color="grey darken-1"
              @click="resetMonthBudgetDraft"
              >Reset to Config</v-btn
            >
          </div>
          <p
            v-if="budgetEditorSortBy === 'budgetDelta'"
            class="caption grey--text text--darken-1 mb-3"
          >
            <strong>Over budget (Δ)</strong> matches Project Budget Snapshot:
            red and amber first, then on-track by % of cap used.
          </p>
          <v-expansion-panels multiple class="budget-editor-panels mb-3">
            <v-expansion-panel
              v-for="row in sortedBudgetEditorRows"
              :key="`editor-${row.project}`"
              :class="{'amber lighten-5': row.needsOrgBudget}"
            >
              <v-expansion-panel-header class="py-2 align-start">
                <div class="flex-grow-1" style="min-width: 0">
                  <div class="d-flex flex-wrap align-center" style="gap: 8px">
                    <span
                      class="subtitle-2 font-weight-medium"
                      :class="budgetStatusTextClass(row.barStatus)"
                      >{{ row.project }}</span
                    >
                    <v-chip
                      v-if="row.needsOrgBudget"
                      x-small
                      color="orange"
                      text-color="white"
                    >
                      needs org budget
                    </v-chip>
                    <span
                      class="caption grey--text text--darken-1"
                      :class="budgetStatusTextClass(row.barStatus)"
                      >Mo {{ row.monthLoggedLabel }}</span
                    >
                    <span
                      v-if="row.contributors.length"
                      class="caption grey--text text--darken-1"
                    >
                      {{ row.contributors.length }} with time
                    </span>
                  </div>
                  <div
                    v-if="row.budgetBar"
                    class="snapshot-header-bar-wrap mt-2"
                    style="max-width: 480px"
                    @click.stop
                  >
                    <KonekoBudgetRangeBar
                      :current-decimal="row.budgetBar.current"
                      :low-decimal="row.budgetBar.low"
                      :high-decimal="row.budgetBar.high"
                      :target-decimal="row.budgetBar.target"
                      :month-elapsed-percent="monthElapsedPercent"
                      :status="row.barStatus"
                    />
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-2">
                <div
                  class="d-flex flex-wrap align-end mb-3"
                  style="gap: 12px; max-width: 360px"
                  @click.stop
                >
                  <v-text-field
                    dense
                    hide-details
                    outlined
                    label="Min (HH:MM)"
                    placeholder="0:00"
                    style="max-width: 140px"
                    :value="row.low"
                    @change="commitBudgetDraftField(row.project, 'low', $event)"
                    @keyup.enter="
                      commitBudgetDraftField(
                        row.project,
                        'low',
                        $event.target.value
                      )
                    "
                  />
                  <v-text-field
                    dense
                    hide-details
                    outlined
                    label="Max (HH:MM)"
                    placeholder="0:00"
                    style="max-width: 140px"
                    :value="row.high"
                    @change="
                      commitBudgetDraftField(row.project, 'high', $event)
                    "
                    @keyup.enter="
                      commitBudgetDraftField(
                        row.project,
                        'high',
                        $event.target.value
                      )
                    "
                  />
                </div>
                <div class="caption font-weight-medium mb-1">
                  Time by member
                </div>
                <p class="caption grey--text text--darken-1 mb-2">
                  Click a name for timesheet lines and notes this month.
                </p>
                <KonekoAMemberHoursDrilldownTable
                  v-if="row.contributors.length"
                  :project-name="row.project"
                  :rows="row.contributors"
                  :calendar-month-key="calendarMonthKey"
                  :month-elapsed-percent="monthElapsedPercent"
                  :format-hours-cell="formatHoursCell"
                  :project-options="konekoProjectOptions"
                  :reassign-saving-id="timesheetReassignSavingId"
                  :can-reassign-entry="canReassignKonekoEntry"
                  :show-reassign-ui="showKonekoReassignUi"
                  @reassign="reassignKonekoTimesheetEntry"
                />
                <p v-else class="caption grey--text text--darken-1 mb-0">
                  No logged time on this project in {{ calendarMonthKey }}.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-simple-table dense class="budget-editor-totals">
            <thead>
              <tr class="caption grey--text text--darken-1">
                <th></th>
                <th class="text-left" style="max-width: 140px">Min</th>
                <th class="text-left" style="max-width: 140px">Max</th>
                <th class="text-left">Month logged</th>
              </tr>
            </thead>
            <tbody>
              <tr class="font-weight-medium grey lighten-4">
                <td>Total (all projects above)</td>
                <td style="max-width: 140px">
                  {{ budgetEditorColumnTotals.minLabel }}
                </td>
                <td style="max-width: 140px">
                  {{ budgetEditorColumnTotals.maxLabel }}
                </td>
                <td>{{ budgetEditorColumnTotals.usedLabel }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="mt-5">
            <div class="subtitle-2 font-weight-medium mb-1">Historic FTEs</div>
            <p class="caption grey--text text--darken-1 mb-2">
              Org-wide hours logged each month ÷ 150. Current calendar month
              uses live timesheet data; values in brackets project month-end FTE
              from the run-rate at
              {{ historicFteCurrentMonthElapsed }}% through the month.
            </p>
            <div v-if="historicFteLoading" class="caption mb-2">
              Loading historic months…
            </div>
            <p
              v-if="historicFteError"
              class="caption error--text text--darken-1 mb-2"
            >
              {{ historicFteError }}
            </p>
            <div style="overflow-x: auto">
              <v-simple-table dense class="historic-fte-table">
                <thead>
                  <tr>
                    <th class="text-left" style="min-width: 88px"></th>
                    <th
                      v-for="col in historicFteTableMonths"
                      :key="`h-${col.monthKey}`"
                      class="text-center px-2"
                      :class="{
                        'blue-grey lighten-4 font-weight-bold':
                          col.isCurrentMonth,
                      }"
                      style="min-width: 56px"
                    >
                      {{ col.monthLabel }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="font-weight-medium text-no-wrap">Actual FTE</td>
                    <td
                      v-for="col in historicFteTableMonths"
                      :key="`fte-${col.monthKey}`"
                      class="text-center px-2"
                      :class="{
                        'blue-grey lighten-4 font-weight-medium':
                          col.isCurrentMonth,
                      }"
                    >
                      {{ col.fteLabel }}
                    </td>
                  </tr>
                  <tr class="caption grey--text text--darken-1">
                    <td class="text-no-wrap">Hours</td>
                    <td
                      v-for="col in historicFteTableMonths"
                      :key="`hrs-${col.monthKey}`"
                      class="text-center px-2"
                      :class="{
                        'blue-grey lighten-5': col.isCurrentMonth,
                      }"
                    >
                      {{ col.hoursLabel }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel
        v-if="hasKonekoCap(KONEKO_CAP.konekoTeamPersonalBudgets)"
      >
        <v-expansion-panel-header>
          Team personal budgets ({{ calendarMonthKey }})
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="caption grey--text text--darken-1 mb-3">
            Everyone’s saved caps for this month (NekoHR + config fallback).
            Members edit their own rows in
            <strong>My budget</strong> and click Save (each save counts as a
            submission). Double-click Min/Max to edit and save to NekoHR (no
            full page reload). Use <strong>Reload team budgets</strong> to pull
            others’ saves. Expand each <strong>project</strong> (chevron) to see
            timesheet lines — double-click <strong>Hours</strong> to edit (shows
            <strong>edited by …</strong> on the line). Use
            <strong>Reassign to</strong> to move a line. From June 2026, Owen
            and Members with a “Hide from Koneko A from month” date on the Team
            page (e.g. departing staff) are omitted for that month onward.
          </p>
          <p class="caption mb-3">
            <strong>{{ teamBudgetSubmissionStats.submitted }}</strong> submitted
            · <strong>{{ teamBudgetSubmissionStats.pending }}</strong> not
            submitted (of {{ teamBudgetSubmissionStats.total }} on roster)
          </p>
          <div class="d-flex align-center mb-3" style="gap: 8px">
            <v-btn
              small
              outlined
              color="primary"
              :loading="konekoBudgetApiLoading"
              @click="prefetchKonekoBudgetApiForMonth(calendarMonthKey)"
            >
              Reload team budgets
            </v-btn>
          </div>
          <v-expansion-panels multiple class="team-personal-budget-panels">
            <v-expansion-panel
              v-for="group in teamPersonalBudgetGroups"
              :key="`team-budget-${group.memberKey}`"
            >
              <v-expansion-panel-header>
                <div class="d-flex flex-wrap align-center" style="gap: 8px">
                  <span class="subtitle-2 font-weight-medium">{{
                    group.memberLabel
                  }}</span>
                  <span class="caption grey--text text--darken-1">{{
                    group.memberKey
                  }}</span>
                  <v-chip
                    x-small
                    :color="
                      group.submitted
                        ? group.submission.count > 1
                          ? 'amber darken-2'
                          : 'green'
                        : ''
                    "
                    :outlined="!group.submitted"
                    :dark="group.submitted"
                  >
                    {{ group.submissionLabel }}
                  </v-chip>
                  <v-chip
                    v-if="group.timesheetEditChipLabel"
                    x-small
                    color="indigo lighten-4"
                    class="snap-edited-by-chip"
                    :title="`${group.timesheetEditCount} line(s) updated from Koneko A`"
                  >
                    {{ group.timesheetEditChipLabel }}
                  </v-chip>
                  <span class="caption grey--text text--darken-1">{{
                    group.totalsLabel
                  }}</span>
                  <span class="caption"
                    >Used {{ group.monthUsedLabel }} this month</span
                  >
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <KonekoATeamMemberProjectPanels
                  :member-key="group.memberKey"
                  :projects="group.projects"
                  :min-total-label="group.minTotalLabel"
                  :max-total-label="group.maxTotalLabel"
                  :used-total-label="group.usedTotalLabel"
                  :project-options="konekoProjectOptions"
                  :reassign-saving-id="timesheetReassignSavingId"
                  :hours-saving-id="timesheetHoursSavingId"
                  :can-reassign-entry="canReassignKonekoEntry"
                  :can-edit-hours="canEditKonekoTimesheetEntry"
                  :is-editing-hours="isEditingTimesheetHours"
                  :hours-edit-value="timesheetHoursEdit.value"
                  :show-reassign-ui="showKonekoReassignUi"
                  :saving="teamBudgetSaving"
                  @edit-hours="startKonekoTimesheetHoursEdit"
                  @commit-hours-edit="commitKonekoTimesheetHoursEdit"
                  @cancel-hours-edit="cancelKonekoTimesheetHoursEdit"
                  @update:hoursEditValue="timesheetHoursEdit.value = $event"
                  :edit-value="teamBudgetEditValue"
                  :is-editing="isTeamBudgetEditing"
                  :is-project-expanded="
                    (project) =>
                      isTeamBudgetProjectExpanded(group.memberKey, project)
                  "
                  @toggle-project="
                    (project) =>
                      toggleTeamBudgetProject(group.memberKey, project)
                  "
                  @edit-budget="startTeamBudgetEdit"
                  @update:editValue="teamBudgetEditValue = $event"
                  @commit-edit="commitTeamBudgetEdit"
                  @cancel-edit="cancelTeamBudgetEdit"
                  @reassign="
                    (entry, pid, pname, fromProject) =>
                      reassignKonekoTimesheetEntry(entry, pid, pname, {
                        memberKey: group.memberKey,
                        fromProject,
                      })
                  "
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <div
            v-if="teamPersonalBudgetGroups.length"
            class="team-budget-team-totals mt-4"
          >
            <div
              class="team-project-col-head caption grey--text text--darken-1 mb-1"
            >
              <span>Project</span>
              <span class="team-project-hours-cell">Used (month)</span>
              <span class="team-project-hours-cell">Min (HH:MM)</span>
              <span class="team-project-hours-cell">Max (HH:MM)</span>
            </div>
            <div
              class="team-project-col-head team-project-totals-row font-weight-medium grey lighten-4 py-2"
            >
              <span class="team-total-label">
                <v-icon
                  x-small
                  class="team-total-label-spacer"
                  aria-hidden="true"
                >
                  mdi-chevron-right
                </v-icon>
                All members
              </span>
              <span class="team-project-hours-cell">{{
                teamPersonalBudgetColumnTotals.usedLabel
              }}</span>
              <span class="team-project-hours-cell">{{
                teamPersonalBudgetColumnTotals.minLabel
              }}</span>
              <span class="team-project-hours-cell">{{
                teamPersonalBudgetColumnTotals.maxLabel
              }}</span>
            </div>
          </div>
          <p
            v-if="!teamPersonalBudgetGroups.length"
            class="caption grey--text text--darken-1 mb-0"
          >
            No personal caps for this month yet.
          </p>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel
        v-if="
          hasKonekoCap(KONEKO_CAP.konekoMemberBreakdownAll) ||
          visibleMembers.length
        "
      >
        <v-expansion-panel-header>
          Member Breakdown ({{ visibleMembers.length }})
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
            <span class="caption grey--text text--darken-1">Sort by</span>
            <v-btn-toggle
              v-model="memberBreakdownSortBy"
              mandatory
              dense
              color="primary"
              @change="persistMemberBreakdownSortBy"
            >
              <v-btn small value="name">Name (default)</v-btn>
              <v-btn small value="timeSpent">Time spent</v-btn>
            </v-btn-toggle>
          </div>
          <v-expansion-panels multiple>
            <v-expansion-panel
              v-for="member in visibleMembers"
              :key="member.username"
            >
              <v-expansion-panel-header>
                <div>
                  <div
                    class="subtitle-2 font-weight-medium"
                    :class="memberBreakdownWorstStatusClass(member)"
                  >
                    {{ member.name }} at {{ memberHeadlineHours(member) }}
                    {{ memberHeadlineLimitClause(member) }}
                  </div>
                  <div class="caption grey--text text--darken-1">
                    Last logged: {{ member.lastLogged }} · Days since daily
                    report:
                    {{ member.daysSinceDailyReport }}
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table v-if="member.allocations.length" dense>
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Month</th>
                      <th>Personal limit</th>
                    </tr>
                  </thead>
                  <tbody
                    v-for="line in member.allocations"
                    :key="member.username + '-' + line.project"
                  >
                    <tr :class="budgetStatusTextClass(line.barStatus)">
                      <td>
                        <button
                          type="button"
                          class="snap-member-toggle"
                          @click="
                            toggleMemberBreakdownProject(
                              member.username,
                              line.project
                            )
                          "
                        >
                          <v-icon x-small class="mr-1">
                            {{
                              isMemberBreakdownProjectExpanded(
                                member.username,
                                line.project
                              )
                                ? "mdi-chevron-down"
                                : "mdi-chevron-right"
                            }}
                          </v-icon>
                          {{ line.project
                          }}<span v-if="line.project === 'unknown'" class="ml-1"
                            >***</span
                          >
                        </button>
                      </td>
                      <td>{{ formatHoursCell(line.monthLogged) }}</td>
                      <td>
                        <span v-if="line.target" class="d-flex align-center">
                          <span
                            class="grey--text text--darken-1 mr-1"
                            style="font-size: 11px"
                            >●</span
                          >
                          <span>{{ line.percent }} of {{ line.target }}</span>
                        </span>
                        <span
                          v-else
                          class="d-flex align-center orange--text text--darken-2"
                        >
                          <span class="mr-1 font-weight-medium">FLEX</span>
                        </span>
                        <KonekoBudgetRangeBar
                          v-if="line.personalBudgetBar && line.target"
                          class="mt-1"
                          :current-decimal="line.personalBudgetBar.current"
                          :low-decimal="line.personalBudgetBar.low"
                          :high-decimal="line.personalBudgetBar.high"
                          :target-decimal="line.personalBudgetBar.target"
                          :month-elapsed-percent="monthElapsedPercent"
                          :status="line.barStatus"
                        />
                      </td>
                    </tr>
                    <tr
                      v-show="
                        isMemberBreakdownProjectExpanded(
                          member.username,
                          line.project
                        )
                      "
                    >
                      <td colspan="3" class="py-2">
                        <div
                          v-if="line.entries && line.entries.length"
                          class="snap-entries-wrap"
                        >
                          <KonekoATimesheetEntriesTable
                            :entries="line.entries"
                            :project-options="konekoProjectOptions"
                            :reassign-saving-id="timesheetReassignSavingId"
                            :hours-saving-id="timesheetHoursSavingId"
                            :can-reassign-entry="canReassignKonekoEntry"
                            :can-edit-hours="canEditKonekoTimesheetEntry"
                            :is-editing-hours="isEditingTimesheetHours"
                            :hours-edit-value="timesheetHoursEdit.value"
                            :show-reassign-ui="showKonekoReassignUi"
                            @reassign="reassignKonekoTimesheetEntry"
                            @edit-hours="startKonekoTimesheetHoursEdit"
                            @commit-hours-edit="commitKonekoTimesheetHoursEdit"
                            @cancel-hours-edit="cancelKonekoTimesheetHoursEdit"
                            @update:hoursEditValue="
                              timesheetHoursEdit.value = $event
                            "
                          />
                        </div>
                        <p
                          v-else
                          class="caption grey--text text--darken-1 mb-0"
                        >
                          No timesheet lines this month (reload if you expect
                          rows).
                        </p>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="member-breakdown-totals-row font-weight-medium">
                      <td>Total</td>
                      <td>{{ memberBreakdownMonthTotalLabel(member) }}</td>
                      <td>{{ memberBreakdownCapTotalLabel(member) }}</td>
                    </tr>
                  </tfoot>
                </v-simple-table>
                <div
                  v-if="memberHasUnknownProject(member)"
                  class="caption grey--text mt-2"
                >
                  *** Hours on unmapped / unknown project IDs.
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script>
import KonekoBudgetRangeBar from "@/components/Koneko/KonekoBudgetRangeBar.vue";
import KonekoAProjectBudgetSnapshotPanels from "@/components/Koneko/KonekoAProjectBudgetSnapshotPanels.vue";
import KonekoAMemberHoursDrilldownTable from "@/components/Koneko/KonekoAMemberHoursDrilldownTable.vue";
import KonekoABudgetHistoryPanel from "@/components/Koneko/KonekoABudgetHistoryPanel.vue";
import KonekoATimesheetEntriesTable from "@/components/Koneko/KonekoATimesheetEntriesTable.vue";
import KonekoATeamMemberProjectPanels from "@/components/Koneko/KonekoATeamMemberProjectPanels.vue";
import DevApiWiringHint from "@/components/DevApiWiringHint.vue";
import KonekoAMonthNavigator from "@/components/Koneko/KonekoAMonthNavigator.vue";
import konekoAWorkloadMixin from "@/mixins/konekoAWorkloadMixin";
import {KONEKO_CAP} from "@/config/konekoVisibility";

function parseMonthKey(monthKey) {
  const m = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12)
    return null;
  return {year: y, month: mo};
}

export default {
  name: "KonekoAView",
  components: {
    DevApiWiringHint,
    KonekoAMonthNavigator,
    KonekoBudgetRangeBar,
    KonekoAProjectBudgetSnapshotPanels,
    KonekoAMemberHoursDrilldownTable,
    KonekoABudgetHistoryPanel,
    KonekoATimesheetEntriesTable,
    KonekoATeamMemberProjectPanels,
  },
  mixins: [konekoAWorkloadMixin],
  data() {
    return {
      /** Mount heavy panels after navigation commits — avoids aborted transition → Dashboard. */
      konekoHeavyUiReady: false,
      /** Panel indices to expand on load; 0 = My budget when that panel is shown. */
      konekoAOpenPanels: [0],
    };
  },
  created() {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.konekoHeavyUiReady = true;
      });
    });
  },
  async mounted() {
    const focusMyBudget = String(this.$route.query.focus || "") === "my-budget";
    const personalScope = this.$route.query.scope === "self";
    this.initKonekoCalendarMonthFromRoute();
    await this.loadMonthBudgetDraft();
    await this.loadMyMemberTargetsDraft();
    await this.ensureKonekoProjectsCatalog();
    await this.loadKonekoAData();
    this.syncKonekoAOpenPanels({focusMyBudget, personalScope});
  },
  watch: {
    async selectedMonthKey() {
      await this.loadMonthBudgetDraft();
      await this.loadMyMemberTargetsDraft();
      await this.ensureKonekoProjectsCatalog();
      await this.loadKonekoAData(true);
    },
    "$route.query.month"(month) {
      const mk = String(month || "").trim();
      if (parseMonthKey(mk) && mk !== this.selectedMonthKey) {
        this.selectedMonthKey = mk;
      }
    },
    "$route.query": {
      deep: true,
      handler() {
        this.loadKonekoAData();
      },
    },
    viewAsPreviewActive() {
      this.syncKonekoAOpenPanels({
        focusMyBudget: String(this.$route.query.focus || "") === "my-budget",
        personalScope: this.$route.query.scope === "self",
      });
    },
  },
  methods: {
    syncKonekoAOpenPanels({focusMyBudget = false, personalScope = false} = {}) {
      const visibleIndices = [];
      let idx = 0;
      if (this.showMyBudgetPanel) {
        visibleIndices.push(idx);
        idx += 1;
      }
      const panelCaps = [
        KONEKO_CAP.konekoProjectSnapshot,
        KONEKO_CAP.konekoCrossComparison,
        KONEKO_CAP.konekoOrgBudgetEditor,
        KONEKO_CAP.konekoTeamPersonalBudgets,
      ];
      panelCaps.forEach((cap) => {
        if (this.hasKonekoCap(cap)) {
          visibleIndices.push(idx);
        }
        idx += 1;
      });
      const showMemberBreakdown =
        this.hasKonekoCap(KONEKO_CAP.konekoMemberBreakdownAll) ||
        this.visibleMembers.length;
      if (showMemberBreakdown) {
        visibleIndices.push(idx);
        idx += 1;
      }

      if (focusMyBudget && this.showMyBudgetPanel) {
        this.konekoAOpenPanels = [0];
        return;
      }
      const expandAll =
        !this.viewAsPreviewActive && this.effectiveAdminView && !personalScope;
      this.konekoAOpenPanels = expandAll
        ? visibleIndices
        : visibleIndices.length
        ? [visibleIndices[0]]
        : [];
    },
  },
};
</script>

<style scoped>
.snapshot-header-bar-wrap {
  width: 100%;
  max-width: 640px;
}

.team-budget-editable {
  cursor: pointer;
  min-width: 5rem;
}

.team-budget-editable:hover {
  background: rgba(25, 118, 210, 0.06);
}

.snap-member-toggle {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  text-align: left;
}

.snap-entries-wrap {
  padding: 8px 12px 12px 28px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
}

.snap-entry-notes {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.35;
  max-width: 42rem;
}

.team-project-col-head {
  display: grid;
  grid-template-columns: minmax(160px, 1fr) 5.5rem 5.5rem 5.5rem;
  gap: 8px 12px;
  align-items: center;
}

.team-project-hours-cell {
  text-align: right;
  justify-self: end;
  font-variant-numeric: tabular-nums;
}

.team-total-label {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.team-total-label-spacer {
  visibility: hidden;
  margin-right: 4px;
}

.team-project-totals-row {
  border-radius: 4px;
}

.member-breakdown-totals-row {
  background: rgba(0, 0, 0, 0.04);
}

.member-breakdown-totals-row td {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.my-budget-editor-table >>> table,
.koneko-budget-cap-table >>> table {
  table-layout: fixed;
  width: 100%;
}

.my-budget-editor-table >>> th,
.my-budget-editor-table >>> td,
.koneko-budget-cap-table >>> th,
.koneko-budget-cap-table >>> td {
  font-size: 12px;
  vertical-align: top;
}

.my-budget-editor-table >>> .koneko-col-project,
.koneko-budget-cap-table >>> .koneko-col-project {
  width: 38%;
}

.my-budget-editor-table >>> .koneko-col-hours,
.koneko-budget-cap-table >>> .koneko-col-hours {
  width: 18%;
}

.my-budget-editor-table >>> .koneko-col-remove {
  width: 10%;
}
</style>
